import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Navbar = ({ darkTheme, setDarkTheme }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoHovered, setLogoHovered] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = darkTheme ? "#0a0a0a" : "#ffffff";
    document.body.style.color = darkTheme ? "#fff" : "#111";
  }, [darkTheme]);

  const navItems = [
    { path: "/", name: "Home" },
    { path: "/about", name: "About" },
    { path: "/artists", name: "Artists" },
    { path: "/services", name: "Services" },
    { path: "/contact", name: "Contact" },
  ];

  const bgColor = darkTheme
    ? scrolled ? "rgba(10, 10, 10, 0.95)" : "rgba(10, 10, 10, 0.8)"
    : scrolled ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.8)";

  return (
    <>
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          padding: "1.2rem 0",
          zIndex: 1000,
          transition: "all 0.3s ease",
          backgroundColor: bgColor,
          backdropFilter: "blur(10px)",
          boxShadow: scrolled
            ? darkTheme ? "0 4px 20px rgba(0, 0, 0, 0.5)" : "0 4px 20px rgba(0, 0, 0, 0.1)"
            : "none",
          borderBottom: darkTheme ? "none" : "1px solid #e5e5e5",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "0 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <div
            style={{
              zIndex: 1001,
              backgroundColor: "transparent",
              padding: "8px 12px",
              borderRadius: "8px",
              transition: "all 0.3s ease",
              transform: logoHovered ? "scale(1.05)" : "scale(1)",
            }}
            onMouseEnter={() => setLogoHovered(true)}
            onMouseLeave={() => setLogoHovered(false)}
          >
            <Link to="/" style={{ display: "block" }}>
              <svg
                width="120"
                height="40"
                viewBox="0 0 200 80"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transition: "all 0.3s ease",
                }}
              >
                {/* FA text */}
                <text
                  x="0"
                  y="40"
                  fill={logoHovered ? "#ff0000" : darkTheme ? "#ffffff" : "#000000"}
                  fontFamily="Poppins, Arial, sans-serif"
                  fontSize="30"
                  fontWeight="700"
                  letterSpacing="1"
                  style={{ transition: "fill 0.3s ease" }}
                >
                  FA
                </text>

                {/* X text - changes on hover based on theme */}
                <text
                  x="40"
                  y="41"
                  fill={logoHovered ? (darkTheme ? "#ffffff" : "#000000") : "#ff0000"}
                  fontFamily="Poppins, Arial, sans-serif"
                  fontSize="36"
                  fontWeight="700"
                  style={{ transition: "fill 0.3s ease" }}
                >
                  X
                </text>

                {/* ON text */}
                <text
                  x="65"
                  y="40"
                  fill={logoHovered ? "#ff0000" : darkTheme ? "#ffffff" : "#000000"}
                  fontFamily="Poppins, Arial, sans-serif"
                  fontSize="30"
                  fontWeight="700"
                  letterSpacing="1"
                  style={{ transition: "fill 0.3s ease" }}
                >
                  ON
                </text>

                {/* Entertainment text */}
                <text
                  x="0"
                  y="70"
                  fill={logoHovered ? "#ff0000" : darkTheme ? "#ffffff" : "#000000"}
                  fontFamily="Poppins, Arial, sans-serif"
                  fontSize="25"
                  fontWeight="700"
                  letterSpacing="0.5"
                  style={{ transition: "fill 0.3s ease" }}
                >
                  Entertainment
                </text>

                {/* TM Symbol with Circle */}
                <g transform="translate(120,15)">
                  <circle
                    cx="0"
                    cy="0"
                    r="6"
                    fill="none"
                    stroke={logoHovered ? "#ff0000" : darkTheme ? "#ffffff" : "#000000"}
                    strokeWidth="1"
                    style={{ transition: "stroke 0.3s ease" }}
                  />
                  <text
                    x="0"
                    y="2"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={logoHovered ? "#ff0000" : darkTheme ? "#ffffff" : "#000000"}
                    fontFamily="Poppins, Arial, sans-serif"
                    fontSize="10"
                    fontWeight="700"
                    style={{ transition: "fill 0.3s ease" }}
                  >
                    ™
                  </text>
                </g>
              </svg>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <ul
            style={{
              listStyle: "none",
              display: "flex",
              gap: "2rem",
              alignItems: "center",
              margin: 0,
              padding: 0,
            }}
            className="desktop-nav"
          >
            {navItems.map(({ path, name }) => (
              <li key={path} style={{ position: "relative" }}>
                <Link
                  to={path}
                  style={{
                    textDecoration: "none",
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    padding: "0.5rem 0",
                    transition: "all 0.3s ease",
                    display: "inline-block",
                    color: location.pathname === path ? "#ff0000" : darkTheme ? "#fff" : "#111",
                    borderBottom:
                      location.pathname === path ? "2px solid #ff0000" : "2px solid transparent",
                  }}
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>

          {/* Theme Toggle */}
          <button
            onClick={() => setDarkTheme(!darkTheme)}
            style={{
              marginLeft: "1rem",
              padding: "0.6rem 0.8rem",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontSize: "1.2rem",
              transition: "all 0.3s ease",
              backgroundColor: darkTheme ? "#fff" : "#111",
              color: darkTheme ? "#111" : "#fff",
              zIndex: 1001,
            }}
            className="theme-toggle"
          >
            {darkTheme ? "☀️" : "🌙"}
          </button>

          {/* Mobile Hamburger */}
          <div
            style={{
              display: "none",
              flexDirection: "column",
              gap: "6px",
              cursor: "pointer",
              zIndex: 1001,
            }}
            className="hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <div
              style={{
                width: "28px",
                height: "3px",
                transition: "all 0.3s ease",
                borderRadius: "2px",
                backgroundColor: darkTheme ? "#fff" : "#111",
                transform: menuOpen ? "rotate(45deg) translateY(9px)" : "none",
              }}
            ></div>
            <div
              style={{
                width: "28px",
                height: "3px",
                transition: "all 0.3s ease",
                borderRadius: "2px",
                backgroundColor: darkTheme ? "#fff" : "#111",
                opacity: menuOpen ? 0 : 1,
              }}
            ></div>
            <div
              style={{
                width: "28px",
                height: "3px",
                transition: "all 0.3s ease",
                borderRadius: "2px",
                backgroundColor: darkTheme ? "#fff" : "#111",
                transform: menuOpen ? "rotate(-45deg) translateY(-9px)" : "none",
              }}
            ></div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {menuOpen && (
        <ul
          style={{
            position: "fixed",
            top: "80px",
            left: 0,
            width: "100%",
            height: "calc(100vh - 80px)",
            backgroundColor: darkTheme ? "rgba(10, 10, 10, 0.98)" : "rgba(255, 255, 255, 0.98)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "2.5rem",
            backdropFilter: "blur(10px)",
            listStyle: "none",
            margin: 0,
            padding: 0,
            zIndex: 999,
          }}
          className="mobile-nav"
        >
          {navItems.map(({ path, name }) => (
            <li key={path}>
              <Link
                to={path}
                style={{
                  textDecoration: "none",
                  fontWeight: "500",
                  fontSize: "1.2rem",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  padding: "0.5rem 0",
                  transition: "all 0.3s ease",
                  display: "inline-block",
                  color: location.pathname === path ? "#ff0000" : darkTheme ? "#fff" : "#111",
                  borderBottom:
                    location.pathname === path ? "2px solid #ff0000" : "2px solid transparent",
                }}
                onClick={() => setMenuOpen(false)}
              >
                {name}
              </Link>
            </li>
          ))}
        </ul>
      )}

      <style>
        {`
          @media (max-width: 968px) {
            .desktop-nav {
              display: none !important;
            }
            .hamburger {
              display: flex !important;
            }
          }
          @media (min-width: 969px) {
            .mobile-nav {
              display: none !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default Navbar;